/**
 * LaunchCurtain: holds the dark launch screen over the app until auth has
 * primed, then fades it out (0.3s) instead of cutting to the page. Children
 * render only once `ready` is true, so no route decides before the token
 * check resolves. Spec: `brand-redesign-docs/09-LOADER.md`.
 *
 * The fade is a CSS transition and the unmount is a timer, so the curtain
 * always clears: a framer exit animation that never finished (seen on a hard
 * refresh of /login) used to leave it covering the page and eating clicks.
 */

import { useReducedMotion } from "framer-motion";
import { useEffect, useState, type ReactNode } from "react";

import { BrandReveal } from "@/components/brand/BrandReveal";

const FADE_MS = 300;

export function LaunchCurtain({ ready, children }: { ready: boolean; children: ReactNode }) {
  const reduce = useReducedMotion();
  const [gone, setGone] = useState(ready);

  useEffect(() => {
    if (!ready) {
      setGone(false);
      return;
    }
    const timer = window.setTimeout(() => setGone(true), reduce ? 0 : FADE_MS);
    return () => window.clearTimeout(timer);
  }, [ready, reduce]);

  return (
    <>
      {gone ? null : <BrandReveal variant="launch" leaving={ready} />}
      {ready ? children : null}
    </>
  );
}
